import { track } from '@affine/track';
import { Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';

import type { DocsService } from '../../doc';
import {
  buildDocsSearchNavigation,
  createDocsSearchNavigationResolvers,
  type DocsSearchNavigablePayload,
  openDocsSearchNavigation,
} from '../../docs-search/services/source-navigation';
import type { NavigationPanelRevealService } from '../../navigation-panel';
import type { WorkbenchService } from '../../workbench';
import type { WorkspaceService } from '../../workspace';
import { CollectionsQuickSearchSession } from '../impls/collections';
import { CommandsQuickSearchSession } from '../impls/commands';
import { CreationQuickSearchSession } from '../impls/creation';
import { DocsQuickSearchSession } from '../impls/docs';
import { LinksQuickSearchSession } from '../impls/links';
import { MailQuickSearchSession } from '../impls/mail';
import { RecentDocsQuickSearchSession } from '../impls/recent-docs';
import { TagsQuickSearchSession } from '../impls/tags';
import type { AdvancedSearchService } from './advanced-search';
import type { QuickSearchService } from './quick-search';

export type CMDKQuickSearchProductFeatures = {
  collectionsEnabled?: boolean;
  tagsEnabled?: boolean;
};

export class CMDKQuickSearchService extends Service {
  constructor(
    private readonly advancedSearchService: AdvancedSearchService,
    private readonly quickSearchService: QuickSearchService,
    private readonly workbenchService: WorkbenchService,
    private readonly docsService: DocsService,
    private readonly navigationPanelRevealService: NavigationPanelRevealService,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  toggle(productFeatures: CMDKQuickSearchProductFeatures = {}) {
    if (this.advancedSearchService.open$.value) {
      this.close();
    } else if (this.quickSearchService.quickSearch.show$.value) {
      this.close();
    } else if (this.advancedSearchService.prefersAdvanced()) {
      this.advancedSearchService.open();
    } else {
      this.openCompact('', productFeatures);
    }
  }

  close() {
    this.advancedSearchService.close();
    if (this.quickSearchService.quickSearch.show$.value) {
      this.quickSearchService.quickSearch.hide();
    }
  }

  openCompact(
    defaultQuery = '',
    {
      collectionsEnabled = false,
      tagsEnabled = false,
    }: CMDKQuickSearchProductFeatures = {}
  ) {
    this.advancedSearchService.close();
    this.quickSearchService.quickSearch.show(
      [
        this.framework.createEntity(RecentDocsQuickSearchSession),
        collectionsEnabled
          ? this.framework.createEntity(CollectionsQuickSearchSession)
          : null,
        this.framework.createEntity(CommandsQuickSearchSession),
        this.framework.createEntity(CreationQuickSearchSession),
        this.framework.createEntity(DocsQuickSearchSession),
        this.framework.createEntity(MailQuickSearchSession),
        this.framework.createEntity(LinksQuickSearchSession),
        tagsEnabled
          ? this.framework.createEntity(TagsQuickSearchSession)
          : null,
      ].filter((session): session is NonNullable<typeof session> => !!session),
      result => {
        if (!result) {
          return;
        }

        if (result.source === 'commands') {
          result.payload.run()?.catch(err => {
            console.error(err);
          });
          return;
        }

        if (result.source === 'link') {
          const { docId, blockIds, elementIds, mode } = result.payload;
          this.workbenchService.workbench.openDoc({
            docId,
            blockIds,
            elementIds,
            mode,
            // refreshKey forces focusAt$ to re-emit even for same-doc navigation
            refreshKey: nanoid(8),
          });
          this.navigationPanelRevealService.revealDoc(docId, {
            source: 'compact-search',
            workspaceId: this.workspaceService.workspace.id,
          });
          return;
        }

        if (result.source === 'mail') {
          this.workbenchService.workbench.open(result.payload.route);
          return;
        }

        if (result.source === 'recent-doc' || result.source === 'docs') {
          const payload = result.payload as DocsSearchNavigablePayload;
          let skippedMissingBlock: {
            blockId: string;
            docId: string;
            sourceType: string | undefined;
          } | null = null;
          const resolvers = createDocsSearchNavigationResolvers(
            this.docsService
          );

          const navigation = buildDocsSearchNavigation(payload, {
            ...resolvers,
            resolveBlockStatus: (docId, blockId) => {
              const status = resolvers.resolveBlockStatus(docId, blockId);
              if (status === 'missing') {
                skippedMissingBlock = {
                  blockId,
                  docId,
                  sourceType: payload.sourceType,
                };
              }
              return status;
            },
          });
          if (!navigation) {
            if (skippedMissingBlock) {
              console.warn(
                '[CMDKQuickSearchService] Skipping stale search result for missing block',
                skippedMissingBlock
              );
            }
            return;
          }

          result.source === 'recent-doc' && track.$.cmdk.recent.recentDocs();
          result.source === 'docs' && track.$.cmdk.results.searchResultsDocs();

          openDocsSearchNavigation(this.workbenchService.workbench, navigation);
          this.navigationPanelRevealService.revealDoc(navigation.docId, {
            source: 'compact-search',
            workspaceId: this.workspaceService.workspace.id,
          });
          return;
        }

        if (result.source === 'collections') {
          if (!collectionsEnabled) {
            return;
          }
          this.workbenchService.workbench.openCollection(
            result.payload.collectionId
          );
          return;
        }

        if (result.source === 'tags') {
          if (!tagsEnabled) {
            return;
          }
          this.workbenchService.workbench.openTag(result.payload.tagId);
          return;
        }

        if (result.source === 'creation') {
          if (result.id === 'creation:create-page') {
            const newDoc = this.docsService.createDoc({
              primaryMode: 'page',
              title: result.payload.title,
              createInWorkspaceRoot: true,
              userInitiated: true,
            });

            this.workbenchService.workbench.openDoc(newDoc.id);
            this.navigationPanelRevealService.revealDoc(newDoc.id, {
              source: 'compact-search',
              workspaceId: this.workspaceService.workspace.id,
            });
          } else if (result.id === 'creation:create-edgeless') {
            const newDoc = this.docsService.createDoc({
              primaryMode: 'edgeless',
              title: result.payload.title,
              createInWorkspaceRoot: true,
              userInitiated: true,
            });
            this.workbenchService.workbench.openDoc(newDoc.id);
            this.navigationPanelRevealService.revealDoc(newDoc.id, {
              source: 'compact-search',
              workspaceId: this.workspaceService.workspace.id,
            });
          }
          return;
        }
      },
      {
        placeholder: {
          i18nKey: 'com.streamlain.cmdk.docs.placeholder',
        },
        defaultQuery,
        allowAdvanced: true,
      }
    );
  }
}
